import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/migrated/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PlusCircle } from 'lucide-react';

const createFunctionSchema = z.object({
    name: z.string().min(1, 'Function name is required').trim(),
});

type CreateFunctionFormValues = z.infer<typeof createFunctionSchema>;

interface CreateFunctionFormProps {
    onSubmit: (name: string) => void;
    onCancel: () => void;
}

export const CreateFunctionForm: React.FC<CreateFunctionFormProps> = ({ onSubmit, onCancel }) => {
    const form = useForm<CreateFunctionFormValues>({
        resolver: zodResolver(createFunctionSchema),
        defaultValues: {
            name: '',
        },
    });

    const handleSubmit = (values: CreateFunctionFormValues) => {
        onSubmit(values.name);
        form.reset();
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-2">
                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Input
                                    placeholder="Function name..."
                                    autoFocus
                                    autoComplete="off"
                                    data-1p-ignore
                                    data-lpignore="true"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className="flex gap-2">
                    <Button size="sm" type="submit">
                        Create
                    </Button>
                    <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

interface CreateFunctionButtonProps {
    onClick: () => void;
}

export const CreateFunctionButton: React.FC<CreateFunctionButtonProps> = ({ onClick }) => (
    <Button size="sm" variant="outline" onClick={onClick} className="w-full gap-1">
        <PlusCircle className="w-4 h-4" />
        Create New Function
    </Button>
);
